import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {
	pendingApprovals: [],
	pendingTrips: [],
	allAdhocRequests: [],
	loading: {
		pendingApprovals: false,
		pendingTrips: false,
		allAdhocRequests: false,
	},
	error: {
		pendingApprovals: null,
		pendingTrips: null,
		allAdhocRequests: null,
	},
};

export const advancePaymentSlice = createSlice({
	name: 'advancePayment',
	initialState,
	reducers: {
		setPendingApprovals: (state, action) => {
			state.pendingApprovals = action.payload;
			state.loading.pendingApprovals = false;
			state.error.pendingApprovals = null;
		},
		setPendingTrips: (state, action) => {
			state.pendingTrips = action.payload;
			state.loading.pendingTrips = false;
			state.error.pendingTrips = null;
		},
		setAllAdhocRequests: (state, action) => {
			state.allAdhocRequests = action.payload;
			state.loading.allAdhocRequests = false;
			state.error.allAdhocRequests = null;
		},
		setLoading: (state, action) => {
			const { tab, loading } = action.payload;
			state.loading[tab] = loading;
		},
		setError: (state, action) => {
			const { tab, error } = action.payload;
			state.error[tab] = error;
			state.loading[tab] = false;
		},
	},
});

export const { setPendingApprovals, setPendingTrips, setAllAdhocRequests, setLoading, setError } =
	advancePaymentSlice.actions;

// Async thunk for fetching pending approvals
export const fetchPendingApprovals =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	// eslint-disable-next-line sonarjs/cognitive-complexity
	async (dispatch) => {
		dispatch(setLoading({ tab: 'pendingApprovals', loading: true }));
		try {
			const filterBy = {};

			if (query_str) filterBy.query_str = query_str;
			if (city) filterBy.city = city;
			if (client_id) filterBy.client_id = client_id;
			if (project_id) filterBy.project_id = project_id;
			if (vendor_id) filterBy.vendor_id = vendor_id;
			if (trip_status) filterBy.trip_status = trip_status;
			if (business_unit_ids && business_unit_ids.length > 0)
				filterBy.business_unit_ids = business_unit_ids;
			if (from_date) filterBy.from_date = from_date;
			if (to_date) filterBy.to_date = to_date;

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'adhoc_request',
					offset,
					limit,
					filter_by: {
						tab: 'pending_approvals',
						...filterBy,
						...rest,
					},
				},
			};

			const response = await API.common(CREDENTIALS);
			dispatch(
				setPendingApprovals({
					rows: response?.data || [],
					count: response?.meta?.count || 0,
				})
			);
			return {
				rows: response?.data || [],
				count: response?.meta?.count || 0,
			};
		} catch (error) {
			dispatch(
				setError({
					tab: 'pendingApprovals',
					error: error.message || 'Failed to fetch pending approvals',
				})
			);
			throw error;
		}
	};

// Async thunk for fetching pending trips
export const fetchPendingTrips =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	// eslint-disable-next-line sonarjs/cognitive-complexity
	async (dispatch) => {
		dispatch(setLoading({ tab: 'pendingTrips', loading: true }));
		try {
			const filterBy = {};

			if (query_str) filterBy.query_str = query_str;
			if (city) filterBy.city = city;
			if (client_id) filterBy.client_id = client_id;
			if (project_id) filterBy.project_id = project_id;
			if (vendor_id) filterBy.vendor_id = vendor_id;
			if (trip_status) filterBy.trip_status = trip_status;
			if (business_unit_ids && business_unit_ids.length > 0)
				filterBy.business_unit_ids = business_unit_ids;
			if (from_date) filterBy.from_date = from_date;
			if (to_date) filterBy.to_date = to_date;

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'adhoc_request',
					offset,
					limit,
					filter_by: {
						tab: 'pending_trips',
						...filterBy,
						...rest,
					},
				},
			};

			const response = await API.common(CREDENTIALS);
			dispatch(
				setPendingTrips({
					rows: response?.data || [],
					count: response?.meta?.count || 0,
				})
			);
			return {
				rows: response?.data || [],
				count: response?.meta?.count || 0,
			};
		} catch (error) {
			dispatch(
				setError({
					tab: 'pendingTrips',
					error: error.message || 'Failed to fetch pending trips',
				})
			);
			throw error;
		}
	};

// Async thunk for fetching all adhoc requests
export const fetchAllAdhocRequests =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	// eslint-disable-next-line sonarjs/cognitive-complexity
	async (dispatch) => {
		dispatch(setLoading({ tab: 'allAdhocRequests', loading: true }));
		try {
			const filterBy = {};

			if (query_str) filterBy.query_str = query_str;
			if (city) filterBy.city = city;
			if (client_id) filterBy.client_id = client_id;
			if (project_id) filterBy.project_id = project_id;
			if (vendor_id) filterBy.vendor_id = vendor_id;
			if (trip_status) filterBy.trip_status = trip_status;
			if (business_unit_ids && business_unit_ids.length > 0)
				filterBy.business_unit_ids = business_unit_ids;
			if (from_date) filterBy.from_date = from_date;
			if (to_date) filterBy.to_date = to_date;

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'adhoc_request',
					offset,
					limit,
					filter_by: {
						tab: 'all',
						...filterBy,
						...rest,
					},
				},
			};

			const response = await API.common(CREDENTIALS);
			dispatch(
				setAllAdhocRequests({
					rows: response?.data || [],
					count: response?.meta?.count || 0,
				})
			);
			return {
				rows: response?.data || [],
				count: response?.meta?.count || 0,
			};
		} catch (error) {
			dispatch(
				setError({
					tab: 'allAdhocRequests',
					error: error.message || 'Failed to fetch all adhoc requests',
				})
			);
			throw error;
		}
	};

// Async thunk for fetching a single adhoc request detail
export const fetchAdhocRequestDetail =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'adhoc_request',
				offset: 0,
				limit: 1,
				fetch_filters: false,
				filter_by: {
					id,
					tab: 'all',
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			rows: response?.data || [],
			count: response?.meta?.count || 0,
		};
	};

// Async thunk for fetching status history
export const fetchStatusHistory =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'adhoc_request_status_history',
				filter_by: {
					id,
				},
			},
		};

		const response = await API.common(CREDENTIALS);
		return {
			data: response?.data || [],
			count: response?.meta?.count || 0,
		};
	};

// Async thunk for exporting adhoc requests
export const exportAdhocRequests =
	({
		tab = 'all',
		from_date,
		to_date,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		query_str,
	}) =>
	async () => {
		const filterBy = { tab };

		if (from_date) filterBy.from_date = from_date;
		if (to_date) filterBy.to_date = to_date;
		if (city) filterBy.city = city;
		if (client_id) filterBy.client_id = client_id;
		if (project_id) filterBy.project_id = project_id;
		if (vendor_id) filterBy.vendor_id = vendor_id;
		if (trip_status) filterBy.trip_status = trip_status;
		if (business_unit_ids && business_unit_ids.length > 0)
			filterBy.business_unit_ids = business_unit_ids;
		if (query_str) filterBy.query_str = query_str;

		const CREDENTIALS = {
			url: '/generate-report',
			data: {
				report_type: 'adhoc_request_export',
				file_type: 'csv',
				data: filterBy,
			},
			aclBlockApiCall: false,
		};

		const response = await API.common(CREDENTIALS);
		return {
			download_url: response?.data?.download_url || null,
		};
	};

// ==================== V2 API Actions ====================

/**
 * V2 Fetch Pending Approvals
 * Uses POST /api/v2/search with v2 request/response format
 */
export const fetchPendingApprovalsV2 =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoading({ tab: 'pendingApprovals', loading: true }));
		try {
			// Import transformation utilities
			const {
				convertAdhocPaginationToV2,
				convertAdhocFiltersToV2,
				transformAdhocListResponseV2ToV1,
			} = await import('../utils/adhocRequestV2Transformations');

			// Convert pagination
			const pagination = convertAdhocPaginationToV2(offset, limit);

			// Build V1 filter object
			const v1Filters = {
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				trip_status,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			};

			// Convert to V2 filters (dates are NOT in filter_by - they go at top level)
			const v2Filters = convertAdhocFiltersToV2(v1Filters, 'pending_approvals');

			// Import date conversion utility
			const { convertAdhocDateToV2 } = await import('../utils/adhocRequestV2Transformations');

			// Build V2 request - dates go at top level, not in filter_by
			const requestData = {
				search_type: 'adhoc_request',
				filter_by: v2Filters,
				pagination,
			};

			// Add dates at top level if they exist
			if (from_date) {
				const v2FromDate = convertAdhocDateToV2(from_date);
				if (v2FromDate) {
					requestData.from_date = v2FromDate;
				}
			}
			if (to_date) {
				const v2ToDate = convertAdhocDateToV2(to_date);
				if (v2ToDate) {
					requestData.to_date = v2ToDate;
				}
			}

			const CREDENTIALS = {
				url: '/search',
				data: requestData,
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);
			const transformedResponse = transformAdhocListResponseV2ToV1(response);

			dispatch(
				setPendingApprovals({
					rows: transformedResponse.rows || [],
					count: transformedResponse.count || 0,
				})
			);

			return transformedResponse;
		} catch (error) {
			dispatch(
				setError({
					tab: 'pendingApprovals',
					error: error.message || 'Failed to fetch pending approvals',
				})
			);
			throw error;
		}
	};

/**
 * V2 Fetch Pending Trips
 * Uses POST /api/v2/search with v2 request/response format
 */
export const fetchPendingTripsV2 =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoading({ tab: 'pendingTrips', loading: true }));
		try {
			// Import transformation utilities
			const {
				convertAdhocPaginationToV2,
				convertAdhocFiltersToV2,
				transformAdhocListResponseV2ToV1,
			} = await import('../utils/adhocRequestV2Transformations');

			// Convert pagination
			const pagination = convertAdhocPaginationToV2(offset, limit);

			// Build V1 filter object
			const v1Filters = {
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				trip_status,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			};

			// Convert to V2 filters (dates are NOT in filter_by - they go at top level)
			const v2Filters = convertAdhocFiltersToV2(v1Filters, 'pending_trips');

			// Import date conversion utility
			const { convertAdhocDateToV2 } = await import('../utils/adhocRequestV2Transformations');

			// Build V2 request - dates go at top level, not in filter_by
			const requestData = {
				search_type: 'adhoc_request',
				filter_by: v2Filters,
				pagination,
			};

			// Add dates at top level if they exist
			if (from_date) {
				const v2FromDate = convertAdhocDateToV2(from_date);
				if (v2FromDate) {
					requestData.from_date = v2FromDate;
				}
			}
			if (to_date) {
				const v2ToDate = convertAdhocDateToV2(to_date);
				if (v2ToDate) {
					requestData.to_date = v2ToDate;
				}
			}

			const CREDENTIALS = {
				url: '/search',
				data: requestData,
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);
			const transformedResponse = transformAdhocListResponseV2ToV1(response);

			dispatch(
				setPendingTrips({
					rows: transformedResponse.rows || [],
					count: transformedResponse.count || 0,
				})
			);

			return transformedResponse;
		} catch (error) {
			dispatch(
				setError({
					tab: 'pendingTrips',
					error: error.message || 'Failed to fetch pending trips',
				})
			);
			throw error;
		}
	};

/**
 * V2 Fetch All Adhoc Requests
 * Uses POST /api/v2/search with v2 request/response format
 */
export const fetchAllAdhocRequestsV2 =
	({
		limit = 10,
		offset = 0,
		query_str,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		from_date,
		to_date,
		...rest
	}) =>
	async (dispatch) => {
		dispatch(setLoading({ tab: 'allAdhocRequests', loading: true }));
		try {
			// Import transformation utilities
			const {
				convertAdhocPaginationToV2,
				convertAdhocFiltersToV2,
				transformAdhocListResponseV2ToV1,
			} = await import('../utils/adhocRequestV2Transformations');

			// Convert pagination
			const pagination = convertAdhocPaginationToV2(offset, limit);

			// Build V1 filter object
			const v1Filters = {
				query_str,
				city,
				client_id,
				project_id,
				vendor_id,
				trip_status,
				business_unit_ids,
				from_date,
				to_date,
				...rest,
			};

			// Convert to V2 filters (no tab/status for 'all', dates are NOT in filter_by - they go at top level)
			const v2Filters = convertAdhocFiltersToV2(v1Filters, 'all');

			// Import date conversion utility
			const { convertAdhocDateToV2 } = await import('../utils/adhocRequestV2Transformations');

			// Build V2 request - dates go at top level, not in filter_by
			const requestData = {
				search_type: 'adhoc_request',
				filter_by: v2Filters,
				pagination,
			};

			// Add dates at top level if they exist
			if (from_date) {
				const v2FromDate = convertAdhocDateToV2(from_date);
				if (v2FromDate) {
					requestData.from_date = v2FromDate;
				}
			}
			if (to_date) {
				const v2ToDate = convertAdhocDateToV2(to_date);
				if (v2ToDate) {
					requestData.to_date = v2ToDate;
				}
			}

			const CREDENTIALS = {
				url: '/search',
				data: requestData,
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);
			const transformedResponse = transformAdhocListResponseV2ToV1(response);

			dispatch(
				setAllAdhocRequests({
					rows: transformedResponse.rows || [],
					count: transformedResponse.count || 0,
				})
			);

			return transformedResponse;
		} catch (error) {
			dispatch(
				setError({
					tab: 'allAdhocRequests',
					error: error.message || 'Failed to fetch all adhoc requests',
				})
			);
			throw error;
		}
	};

/**
 * V2 Fetch Adhoc Request Detail
 * Uses POST /api/v2/search with v2 request/response format
 */
export const fetchAdhocRequestDetailV2 =
	({ id }) =>
	async () => {
		const { convertAdhocPaginationToV2, transformAdhocDetailResponseV2ToV1 } = await import(
			'../utils/adhocRequestV2Transformations'
		);

		const pagination = convertAdhocPaginationToV2(0, 1);
		const filter_by = { id };

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'adhoc_request',
				filter_by,
				pagination,
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		return transformAdhocDetailResponseV2ToV1(response);
	};

/**
 * V2 Create Adhoc Request
 * Uses POST /api/v2/create with v2 request format
 */
export const createAdhocRequestV2 =
	({ adhocRequestData, setErrors }) =>
	async () => {
		const { convertAdhocMomentToV2Date } = await import(
			'../utils/adhocRequestV2Transformations'
		);

		// Transform dates to V2 format
		const v2Data = { ...adhocRequestData };
		if (v2Data.expected_trip_date) {
			v2Data.expected_trip_date = convertAdhocMomentToV2Date(v2Data.expected_trip_date);
		}

		const CREDENTIALS = {
			url: '/create',
			data: {
				create_type: 'adhoc_request',
				data: v2Data,
			},
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS);
	};

/**
 * V2 Update Adhoc Request
 * Uses POST /api/v2/update with v2 request format
 * @param {Object} params
 * @param {Object} params.adhocRequestData - The adhoc request data to update
 * @param {Function} params.setErrors - Error handler function
 * @param {string} params.updateType - Optional update type ('edit_adhoc_request' for editing, 'adhoc_request' for other updates)
 */
export const updateAdhocRequestV2 =
	({ adhocRequestData, setErrors, updateType = 'adhoc_request' }) =>
	async () => {
		const { convertAdhocMomentToV2Date } = await import(
			'../utils/adhocRequestV2Transformations'
		);

		// Transform dates to V2 format
		const v2Data = { ...adhocRequestData };
		if (v2Data.expected_trip_date) {
			v2Data.expected_trip_date = convertAdhocMomentToV2Date(v2Data.expected_trip_date);
		}

		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: updateType, // Use provided update_type (defaults to 'adhoc_request' for backward compatibility)
				data: v2Data,
			},
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS);
	};

/**
 * V2 Export Adhoc Requests
 * Uses POST /api/v2/generate-report with v2 request format
 */
export const exportAdhocRequestsV2 =
	({
		tab = 'all',
		from_date,
		to_date,
		city,
		client_id,
		project_id,
		vendor_id,
		trip_status,
		business_unit_ids,
		query_str,
	}) =>
	async () => {
		// Build data object with tab, dates, and filters
		const data = {
			tab,
			...(from_date && { from_date }),
			...(to_date && { to_date }),
			...(city && { city }),
			...(client_id && { client_id }),
			...(project_id && { project_id }),
			...(vendor_id && { vendor_id }),
			...(trip_status && { trip_status }),
			...(business_unit_ids && business_unit_ids.length > 0 && { business_unit_ids }),
			...(query_str && { query_str }),
		};

		const CREDENTIALS = {
			url: '/generate-report',
			data: {
				report_type: 'adhoc_request_export',
				file_type: 'csv',
				data,
			},
			version: 'v2',
			aclBlockApiCall: false,
		};

		const response = await API.common(CREDENTIALS);
		return {
			download_url: response?.data?.download_url || null,
		};
	};

/**
 * V2 Fetch Status History
 * Uses POST /api/v2/search with v2 request/response format
 */
export const fetchStatusHistoryV2 =
	({ id }) =>
	async () => {
		const { transformStatusHistoryResponseV2ToV1 } = await import(
			'../utils/adhocRequestV2Transformations'
		);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'adhoc_request_status_history',
				filter_by: {
					id,
				},
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		const transformedData = transformStatusHistoryResponseV2ToV1(response);

		return {
			data: transformedData,
			count: transformedData.length || 0,
		};
	};

/**
 * Delete Adhoc Request V2
 * Endpoint: POST /api/v2/update
 * @param {Object} params
 * @param {number} params.id - Adhoc request ID (required)
 * @param {string} params.deletion_reason - Deletion reason (required)
 * @returns {Promise} API response
 */
export const deleteAdhocRequestV2 =
	({ id, deletion_reason = 'Deleted by user' }) =>
	async () => {
		const requestData = {
			update_type: 'delete_adhoc_request',
			data: {
				adhoc_request_id: id,
				deletion_reason: deletion_reason || 'Deleted by user',
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data: requestData,
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		if (response?.status === 'success') {
			toaster.success(response?.message || 'Adhoc request deleted successfully');
		}
		return response;
	};

/**
 * Approve Adhoc Request V2
 * Endpoint: POST /api/v2/update
 * @param {Object} params
 * @param {number} params.id - Adhoc request ID (required)
 * @param {string} params.status - Status to set (e.g. Approved)
 * @param {string} params.comments - Optional comments for approval
 * @returns {Promise} API response
 */
export const approveAdhocRequestV2 =
	({ id, status = 'Approved', comments }) =>
	async () => {
		const requestData = {
			update_type: 'adhoc_request',
			data: {
				id,
				status,
				...(comments && { comments }),
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data: requestData,
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		if (response?.status === 'success') {
			toaster.success(response?.message || 'Adhoc request approved successfully');
		}
		return response;
	};

/**
 * Reject Adhoc Request V2
 * Endpoint: POST /api/v2/update
 * @param {Object} params
 * @param {number} params.id - Adhoc request ID (required)
 * @param {string} params.status - Status to set (default: Rejected)
 * @param {string} params.rejection_reason - Required rejection reason
 * @returns {Promise} API response
 */
export const rejectAdhocRequestV2 =
	({ id, status = 'Rejected', rejection_reason, setErrors }) =>
	async () => {
		const requestData = {
			update_type: 'adhoc_request',
			data: {
				id,
				status,
				...(rejection_reason && { rejection_reason }),
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data: requestData,
			version: 'v2',
			// Without this the 422 handler has nowhere to put the field-level message and
			// drops it, leaving the caller with a null response and no idea what failed.
			...(setErrors ? { setErrors } : {}),
		};

		const response = await API.common(CREDENTIALS);
		if (response?.status === 'success') {
			toaster.success(response?.message || 'Adhoc request rejected successfully');
		}
		return response;
	};

export default advancePaymentSlice.reducer;
